import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import Modal from "@/components/common/modal/Modal"
import {
  useGetOrganizationBUsQuery,
  useDeleteOrganizationBUMutation,
} from "./organizationBUsApiSlice"

const EtDeleteOrganizationBU = ({ id, setActiveAdminView, userOrgId = "" }) => {
  // First Created - 27 Apr 2026
  // Author - Atul Pandey
  // Purpose - To delete / deactivate an Organization BU

  useTitle("Delete Organization BU")

  const { organizationBU } = useGetOrganizationBUsQuery(userOrgId, {
    selectFromResult: ({ data }) => ({
      organizationBU: data?.entities[id],
    }),
  })

  if (!organizationBU) {
    return <PulseLoader color={"#ea580c"} />
  }

  return (
    <EtDeleteOrganizationBUForm
      organizationBU={organizationBU}
      id={id}
      setActiveAdminView={setActiveAdminView}
    />
  )
}

const EtDeleteOrganizationBUForm = ({
  organizationBU,
  id,
  setActiveAdminView,
}) => {
  const navigate = useNavigate()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteMode, setDeleteMode] = useState("soft")

  const [
    deleteOrganizationBU,
    {
      isLoading,
      isSuccess,
      isError,
      error,
    },
  ] = useDeleteOrganizationBUMutation()

  const [formData, setFormData] = useState({
    organizationBUName: organizationBU.organizationBUName || "",
    organizationBUCode: organizationBU.organizationBUCode || "",
    organizationBUDesc: organizationBU.organizationBUDesc || "",
  })

  useEffect(() => {
    if (isSuccess) {
      setFormData({
        organizationBUName: "",
        organizationBUCode: "",
        organizationBUDesc: "",
      })

      if (setActiveAdminView) {
        setActiveAdminView("organizationBUs")
      } else {
        navigate("/organizationBUs")
      }
    }
  }, [isSuccess])

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleOpenModal = (mode = "soft") => {
    setDeleteMode(mode)
    setIsModalOpen(true)
  }

  // const handleOpenModal = (e) => {
  //   if (e) e.preventDefault()
  //   setIsModalOpen(true)
  // }

  const handleCloseModal = () => setIsModalOpen(false)

  const handleConfirm = async (e) => {
    e.preventDefault()

    try {
      await deleteOrganizationBU({ id, deleteMode }).unwrap()

      if (setActiveAdminView) {
        setActiveAdminView("organizationBUs")
      } else {
        navigate("/organizationBUs")
      }
    } catch (error) {
      console.error(error)
    } finally {
      handleCloseModal()
    }
  }

  // const handleConfirm = async (e) => {
  //   e.preventDefault()

  //   try {
  //     await deleteOrganizationBU({ id }).unwrap()

  //     if (setActiveAdminView) {
  //       setActiveAdminView("organizationBUs")
  //     } else {
  //       navigate("/organizationBUs")
  //     }
  //   } catch (error) {
  //     console.error(error)
  //   } finally {
  //     handleCloseModal()
  //   }
  // }

  const handleCancel = () => {
    if (setActiveAdminView) {
      setActiveAdminView("organizationBUs")
    } else {
      navigate("/organizationBUs")
    }
  }

  return (
    <div className="bg-gray-200 p-1 w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Delete Organization BU
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">
          <form
            onSubmit={handleOpenModal}
            className="m-1 p-1 text-xs font-poppins"
          >
            <div id="organizationBUName">
              <input
                type="text"
                name="organizationBUName"
                placeholder="Organization BU name"
                value={formData.organizationBUName}
                onChange={handleChange}
                disabled
                className="w-full rounded p-2 m-1 bg-gray-100"
              />
            </div>

            <div id="organizationBUCode">
              <input
                type="text"
                name="organizationBUCode"
                placeholder="Organization BU code"
                value={formData.organizationBUCode}
                onChange={handleChange}
                disabled
                className="w-full rounded p-2 m-1 bg-gray-100"
              />
            </div>

            <div id="organizationBUDesc">
              <input
                type="text"
                name="organizationBUDesc"
                placeholder="Organization BU description"
                value={formData.organizationBUDesc}
                onChange={handleChange}
                disabled
                className="w-full rounded p-2 m-1 bg-gray-100"
              />
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded m-1 p-2">
              <p className="text-xs text-orange-700">
                You can either deactivate this BU or permanently delete it.
                Deactivation keeps the BU in history. Hard delete removes it permanently.
              </p>
            </div>

            {/* <div className="bg-orange-50 border border-orange-200 rounded m-1 p-2">
              <p className="text-xs text-orange-700">
                This will deactivate the Organization BU. Existing records linked
                to this BU will not be deleted.
              </p>
            </div> */}

            <div id="organizationBUs" className="bg-white m-1 px-1 py-3 rounded">
              {/* <EtBtnFormBasic
                name={"Delete"}
                type={"create"}
                handleClick={handleOpenModal}
              /> */}

              <EtBtnFormBasic
                name={"Make Inactive"}
                type={"clear"}
                handleClick={() => handleOpenModal("soft")}
              />

              <EtBtnFormBasic
                name={"Hard Delete"}
                type={"create"}
                handleClick={() => handleOpenModal("hard")}
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
          </form>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

export default EtDeleteOrganizationBU